import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
// Heavy pages are code-split so the initial JS bundle stays small and the
// first paint on Landing / Dashboard is dramatically faster.
const ProjectDetail = React.lazy(() => import("@/pages/ProjectDetail"));
const Galleries = React.lazy(() => import("@/pages/Galleries"));
const GalleryDetail = React.lazy(() => import("@/pages/GalleryDetail"));
const ClientGallery = React.lazy(() => import("@/pages/ClientGallery"));
const Pricing = React.lazy(() => import("@/pages/Pricing"));
const Analytics = React.lazy(() => import("@/pages/Analytics"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Discover = React.lazy(() => import("@/pages/Discover"));
const PublicProfile = React.lazy(() => import("@/pages/PublicProfile"));
const Invoices = React.lazy(() => import("@/pages/Invoices"));
const PaymentSuccess = React.lazy(() => import("@/pages/PaymentSuccess"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const Feed = React.lazy(() => import("@/pages/Feed"));
const Prints = React.lazy(() => import("@/pages/Prints"));
const Leads = React.lazy(() => import("@/pages/Leads"));
const Albums = React.lazy(() => import("@/pages/Albums"));
const AlbumDetail = React.lazy(() => import("@/pages/AlbumDetail"));
const PublicAlbum = React.lazy(() => import("@/pages/PublicAlbum"));
const Videos = React.lazy(() => import("@/pages/Videos"));
const PublicVideo = React.lazy(() => import("@/pages/PublicVideo"));
const PublicVideoBundle = React.lazy(() => import("@/pages/PublicVideoBundle"));
const PublicGalleryBundle = React.lazy(() => import("@/pages/PublicGalleryBundle"));
import InstallBanner from "@/components/InstallBanner";
import { CookieBanner } from "@/components/ConsentModal";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import UpsellModal from "@/components/UpsellModal";
const ExtendGallery = React.lazy(() => import("@/pages/ExtendGallery"));
const TeamAccept = React.lazy(() => import("@/pages/TeamAccept"));
const Shop = React.lazy(() => import("@/pages/Shop"));
const ShopSell = React.lazy(() => import("@/pages/ShopSell"));
const ShopProduct = React.lazy(() => import("@/pages/ShopProduct"));
const ShopPurchases = React.lazy(() => import("@/pages/ShopPurchases"));
const MarketingKit = React.lazy(() => import("@/pages/MarketingKit"));
const Legal = React.lazy(() => import("@/pages/Legal"));
const VideoAnalytics = React.lazy(() => import("@/pages/VideoAnalytics"));
import "@/index.css";

const RouteFallback = () => <div className="min-h-screen flex items-center justify-center text-stone-500 text-sm">Loading…</div>;

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/showcase" element={<Feed />} />
        <Route path="/p/:slug" element={<PublicProfile />} />
        <Route path="/gallery/:token" element={<ClientGallery />} />
        <Route path="/album/:token" element={<PublicAlbum />} />
        <Route path="/galleries/extend" element={<ExtendGallery />} />
        <Route path="/team/accept" element={<TeamAccept />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/projects" element={<Protected><Projects /></Protected>} />
        <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
        <Route path="/galleries" element={<Protected><Galleries /></Protected>} />
        <Route path="/galleries/:id" element={<Protected><GalleryDetail /></Protected>} />
        <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
        <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
        <Route path="/prints" element={<Protected><Prints /></Protected>} />
        <Route path="/albums" element={<Protected><Albums /></Protected>} />
        <Route path="/albums/:id" element={<Protected><AlbumDetail /></Protected>} />
        <Route path="/videos" element={<Protected><Videos /></Protected>} />
        <Route path="/videos/:videoId/analytics" element={<Protected><VideoAnalytics /></Protected>} />
        <Route path="/v/:token" element={<PublicVideo />} />
        <Route path="/vb/:token" element={<PublicVideoBundle />} />
        <Route path="/gb/:token" element={<PublicGalleryBundle />} />
        <Route path="/leads" element={<Protected><Leads /></Protected>} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/products/:id" element={<ShopProduct />} />
        <Route path="/shop/sell" element={<Protected><ShopSell /></Protected>} />
        <Route path="/shop/purchases" element={<Protected><ShopPurchases /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/marketing" element={<Protected><MarketingKit /></Protected>} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:section" element={<Legal />} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  React.useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(()=>{});
      });
    }
  }, []);
  return (
    <div className="App grain">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <ImpersonationBanner />
            <InstallBanner />
            <CookieBanner />
            <UpsellModal />
            <Toaster
              theme="dark"
              position="top-right"
              richColors
              toastOptions={{
                classNames: {
                  toast: "!bg-stone-950 !border !border-amber-500/20 !text-stone-100 !shadow-[0_10px_30px_-10px_rgba(245,158,11,0.25)]",
                  title: "!font-medium",
                  description: "!text-stone-400",
                  success: "!border-amber-500/40",
                  error: "!border-red-500/40",
                  info: "!border-amber-500/20",
                  closeButton: "!bg-stone-900 !border-stone-700 !text-stone-400",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
